import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eye, Plus, RotateCcw, Save } from "lucide-react";
import { getVzEmailTemplate, saveVzEmailTemplate, resetVzEmailTemplate, previewVzEmail } from "@/lib/verizonSalesApi";
import { EmailSectionEditor, move } from "./EmailSectionEditor";

const FIELDS = [
  ["subject", "Subject (when alert applies)"], ["subject_recovered", "Subject when only back-on-track stores"],
  ["eyebrow", "Top small heading"], ["title", "Title"], ["greeting", "Greeting"], ["intro", "Intro text", true],
  ["info_title", "Left box heading"], ["info_text", "Left box text"], ["alert_title", "Alert box heading"],
  ["alert_text", "Alert box text", true], ["signoff", "Sign-off"],
];

export const EmailTemplateEditor = ({ kind, open, onOpenChange, dealerId }) => {
  const [meta, setMeta] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const close = (o) => {
    if (!o && tpl && JSON.stringify(tpl) !== saved && !window.confirm("Discard unsaved changes to this email?")) return;
    onOpenChange(o);
  };

  useEffect(() => {
    if (!open) return;
    getVzEmailTemplate(kind).then((d) => { setMeta(d); setTpl(d.template); setSaved(JSON.stringify(d.template)); setHtml(""); }).catch(() => toast.error("Failed to load email"));
  }, [open, kind]);

  const set = (k, v) => setTpl((t) => ({ ...t, [k]: v }));
  const setSecs = (s) => set("sections", s);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };
  const preview = () => run(async () => setHtml((await previewVzEmail(kind, { template: tpl, dealer_id: dealerId || null })).html));
  const save = () => run(async () => { const r = await saveVzEmailTemplate(kind, tpl); setTpl(r.template); setSaved(JSON.stringify(r.template)); }, "Email saved");
  const reset = () => window.confirm("Reset this email to the default layout?") &&
    run(async () => { const r = await resetVzEmailTemplate(kind); setTpl(r.template); setSaved(JSON.stringify(r.template)); setHtml(""); }, "Reset to defaults");

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid={`vz-email-editor-${kind}`}>
        <DialogHeader>
          <DialogTitle>Edit email</DialogTitle>
          <DialogDescription className="text-xs">
            Placeholders: {meta?.placeholders.map((p) => `{${p}}`).join(" ")}. Column labels can also use {"{pace_m2}"}…{"{pace_m7}"}, {"{final_m2}"}…, {"{t2}"}…{"{t7}"} (cohort month / MR target).
          </DialogDescription>
        </DialogHeader>
        {!tpl ? <p className="text-sm text-slate-500">Loading…</p> : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              {FIELDS.filter(([k]) => k in tpl).map(([k, l, long]) => (
                <label key={k} className={`text-xs text-slate-600 ${long ? "md:col-span-2" : ""}`}>{l}
                  {long ? <Textarea className="mt-1 text-xs" rows={2} value={tpl[k]} onChange={(e) => set(k, e.target.value)} data-testid={`vz-email-field-${k}`} />
                    : <Input className="mt-1 h-8 text-xs" value={tpl[k]} onChange={(e) => set(k, e.target.value)} data-testid={`vz-email-field-${k}`} />}
                </label>
              ))}
            </div>
            <div className="space-y-3">
              {tpl.sections.map((s, i) => (
                <EmailSectionEditor key={i} sec={s} idx={i} kind={kind} columns={meta.columns} canAdd={meta.can_add_tables}
                  onChange={(ns) => setSecs(tpl.sections.map((x, k) => (k === i ? ns : x)))}
                  onMove={(si, d) => setSecs(move(tpl.sections, si, d))} onRemove={(si) => setSecs(tpl.sections.filter((_, k) => k !== si))} />
              ))}
              {meta.can_add_tables && (
                <Button size="sm" variant="outline" data-testid="vz-email-add-section"
                  onClick={() => setSecs([...tpl.sections, { title: "New table", columns: tpl.sections[0]?.columns.slice(0, 2) || [] }])}>
                  <Plus className="w-4 h-4 mr-1" /> Add table
                </Button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap sticky bottom-0 bg-white py-2 border-t border-slate-100">
              <Button onClick={save} disabled={busy} data-testid="vz-email-save"><Save className="w-4 h-4 mr-1" /> Save email</Button>
              <Button variant="outline" onClick={preview} disabled={busy} data-testid="vz-email-editor-preview"><Eye className="w-4 h-4 mr-1" /> Preview changes</Button>
              <Button variant="ghost" onClick={reset} disabled={busy} data-testid="vz-email-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset to defaults</Button>
            </div>
            {html && <iframe title="email preview" srcDoc={html} className="w-full h-[600px] border border-slate-200 rounded-lg" data-testid="vz-email-editor-iframe" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
